/**
 * ItineraryMapV2 — map for the stay-based itinerary builder.
 * Shows: route polylines between stays, stay markers, activity markers, side trip destinations.
 */
import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { Stay, TransitSegment, SideTrip, RouteSight } from '../../services/api';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const STAY_COLORS = [
  '#0E7490', '#7C3AED', '#B45309', '#065F46', '#9F1239',
  '#1D4ED8', '#92400E', '#155E75', '#4C1D95', '#064E3B',
];

const CATEGORY_EMOJI: Record<string, string> = {
  attraction: '⭐',
  restaurant: '🍽️',
  museum: '🏛️',
  park: '🌳',
  shopping: '🛍️',
  beach: '🏖️',
  viewpoint: '🔭',
  transport: '🚂',
};

const MODE_LABEL: Record<string, string> = {
  drive: '🚗',
  fly: '✈️',
  train: '🚆',
  bus: '🚌',
  ferry: '⛴️',
};

const ROUTE_SIGHT_EMOJI: Record<string, string> = {
  town: '🏘️',
  village: '🏘️',
  viewpoint: '🔭',
  landmark: '📍',
  castle: '🏰',
  waterfall: '💧',
  ruins: '🏛️',
  beach: '🏖️',
  park: '🌳',
  foodstop: '🍽️',
};

const formatDuration = (min: number) => {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

// ORS [lon, lat] → Leaflet [lat, lon]
const orsToLeaflet = (coords: [number, number][]): L.LatLngTuple[] =>
  coords.map(([lon, lat]) => [lat, lon] as L.LatLngTuple);

interface FitBoundsProps { positions: L.LatLngTuple[] }
const FitBounds: React.FC<FitBoundsProps> = ({ positions }) => {
  const map = useMap();
  useEffect(() => {
    if (positions.length === 0) return;
    map.fitBounds(L.latLngBounds(positions), { padding: [40, 40], maxZoom: 14 });
  }, [positions, map]);
  return null;
};

const createStayIcon = (index: number, city: string, color: string): L.DivIcon =>
  L.divIcon({
    className: 'stay-marker',
    html: `<div style="
      background:${color};color:white;border-radius:8px;
      padding:3px 8px;font-size:11px;font-weight:700;
      white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,0.35);
      border:2px solid white;cursor:pointer;
    ">${index + 1}. ${city}</div>`,
    iconSize: [0, 0],
    iconAnchor: [0, 18],
  });

const createActivityIcon = (color: string, category: string): L.DivIcon => {
  const emoji = CATEGORY_EMOJI[category] || '📍';
  return L.divIcon({
    className: 'activity-marker-v2',
    html: `<div style="
      width:26px;height:26px;background:${color};border-radius:50%;
      display:flex;align-items:center;justify-content:center;
      font-size:12px;border:2px solid white;
      box-shadow:0 2px 4px rgba(0,0,0,0.3);
    ">${emoji}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
    popupAnchor: [0, -14],
  });
};

const createSideTripIcon = (): L.DivIcon =>
  L.divIcon({
    className: 'sidetrip-marker',
    html: `<div style="
      background:#F59E0B;color:white;border-radius:6px;
      padding:2px 7px;font-size:10px;font-weight:700;
      white-space:nowrap;box-shadow:0 2px 4px rgba(0,0,0,0.3);
      border:2px solid white;
    ">↪ Side trip</div>`,
    iconSize: [0, 0],
    iconAnchor: [0, 14],
  });

const createTransitLabelIcon = (label: string, clickable: boolean = false): L.DivIcon =>
  L.divIcon({
    className: 'transit-label',
    html: `<div style="
      background:white;border:1px solid #CBD5E1;border-radius:10px;
      padding:2px 7px;font-size:10px;font-weight:600;color:#334155;
      white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,0.15);
      ${clickable ? 'cursor:pointer;' : 'pointer-events:none;'}
    ">${label}</div>`,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });

const createRouteSightIcon = (category: string): L.DivIcon => {
  const emoji = ROUTE_SIGHT_EMOJI[category] || '📍';
  return L.divIcon({
    className: 'route-sight-marker',
    html: `<div style="
      width:24px;height:24px;background:#F59E0B;border-radius:6px;
      display:flex;align-items:center;justify-content:center;
      font-size:12px;border:2px solid white;
      box-shadow:0 1px 3px rgba(0,0,0,0.3);
      transform:rotate(45deg);
    "><span style="transform:rotate(-45deg);">${emoji}</span></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12],
  });
};

export interface ItineraryMapV2Props {
  segments: (Stay | TransitSegment)[];
  sideTrips?: SideTrip[];
  selectedStayId?: string | null;
  selectedTransitId?: string | null;
  className?: string;
  onStayClick?: (stayId: string) => void;
  onTransitClick?: (transitId: string) => void;
  showRouteSights?: boolean;
  onRouteSightClick?: (transitId: string, sight: RouteSight) => void;
}

export const ItineraryMapV2: React.FC<ItineraryMapV2Props> = ({
  segments,
  sideTrips = [],
  selectedStayId,
  selectedTransitId,
  className = '',
  onStayClick,
  onTransitClick,
  showRouteSights = true,
  onRouteSightClick,
}) => {
  const stays = useMemo(() => segments.filter((s): s is Stay => s.type === 'stay'), [segments]);
  const transits = useMemo(() => segments.filter((s): s is TransitSegment => s.type === 'transit'), [segments]);

  // All locatable positions for FitBounds
  const allPositions = useMemo((): L.LatLngTuple[] => {
    const pts: L.LatLngTuple[] = [];
    stays.forEach(s => {
      if (s.location.lat && s.location.lon) pts.push([s.location.lat, s.location.lon]);
      s.activities.forEach(a => {
        if (a.lat && a.lon) pts.push([a.lat, a.lon]);
      });
    });
    transits.forEach(t => {
      (t.route_sights || []).forEach(rs => {
        if (rs.lat && rs.lon) pts.push([rs.lat, rs.lon]);
      });
    });
    sideTrips.forEach(st => {
      const sg = st.suggestion;
      if (sg.lat && sg.lon) pts.push([sg.lat, sg.lon]);
    });
    return pts;
  }, [stays, transits, sideTrips]);

  interface TransitLine {
    transit: TransitSegment;
    fromLat: number; fromLon: number; toLat: number; toLon: number;
    hasGeometry: boolean | undefined; midLat: number; midLon: number; label: string;
  }

  // Transit route lines
  const transitLines = useMemo((): TransitLine[] => {
    const lines: TransitLine[] = [];
    for (const t of transits) {
      const fromLat = t.from_location.lat;
      const fromLon = t.from_location.lon;
      const toLat = t.to_location.lat;
      const toLon = t.to_location.lon;
      if (!fromLat || !fromLon || !toLat || !toLon) continue;

      const hasGeometry = t.geometry && t.geometry.length > 1;
      const midLat = hasGeometry
        ? t.geometry![Math.floor(t.geometry!.length / 2)][1]
        : (fromLat + toLat) / 2;
      const midLon = hasGeometry
        ? t.geometry![Math.floor(t.geometry!.length / 2)][0]
        : (fromLon + toLon) / 2;

      const modeIcon = MODE_LABEL[t.mode] || '🚗';
      const label = t.duration_minutes
        ? `${modeIcon} ${formatDuration(t.duration_minutes)}${t.distance_km ? ` / ${Math.round(t.distance_km)} km` : ''}`
        : `${modeIcon} ${t.mode}`;

      lines.push({ transit: t, fromLat, fromLon, toLat, toLon, hasGeometry, midLat, midLon, label });
    }
    return lines;
  }, [transits]);

  return (
    <div className={`rounded-lg overflow-hidden ${className}`} style={{ height: '100%', width: '100%' }}>
      <MapContainer
        center={[45, 15]}
        zoom={5}
        minZoom={2}
        maxZoom={18}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {allPositions.length > 0 && <FitBounds positions={allPositions} />}

        {/* Transit route lines */}
        {transitLines.map(({ transit, fromLat, fromLon, toLat, toLon, hasGeometry, midLat, midLon, label }) => {
          const isDrive = transit.mode === 'drive';
          const isFlight = transit.mode === 'fly';
          const isFerry = transit.mode === 'ferry';
          const isSelected = transit.id === selectedTransitId;

          const clickHandler = onTransitClick ? { click: () => onTransitClick(transit.id) } : undefined;
          const baseWeight = isSelected ? 6 : 4;

          return (
            <React.Fragment key={transit.id}>
              {isDrive && hasGeometry ? (
                <Polyline
                  positions={orsToLeaflet(transit.geometry!)}
                  pathOptions={{ color: isSelected ? '#DC2626' : '#2563EB', weight: baseWeight, opacity: 0.8 }}
                  eventHandlers={clickHandler}
                />
              ) : isFlight ? (
                <Polyline
                  positions={[[fromLat, fromLon], [toLat, toLon]]}
                  pathOptions={{ color: isSelected ? '#DC2626' : '#4F46E5', weight: isSelected ? 4 : 2, opacity: 0.75, dashArray: '6, 8' }}
                  eventHandlers={clickHandler}
                />
              ) : isFerry ? (
                <Polyline
                  positions={[[fromLat, fromLon], [toLat, toLon]]}
                  pathOptions={{ color: isSelected ? '#DC2626' : '#0EA5E9', weight: isSelected ? 5 : 3, opacity: 0.85, dashArray: '3, 8' }}
                  eventHandlers={clickHandler}
                />
              ) : (
                <Polyline
                  positions={[[fromLat, fromLon], [toLat, toLon]]}
                  pathOptions={{ color: isSelected ? '#DC2626' : '#2563EB', weight: isSelected ? 5 : 3, opacity: 0.7, dashArray: '10, 6' }}
                  eventHandlers={clickHandler}
                />
              )}
              {label && (
                <Marker
                  position={[midLat, midLon]}
                  icon={createTransitLabelIcon(label, !!onTransitClick)}
                  eventHandlers={clickHandler}
                  interactive={!!onTransitClick}
                />
              )}
            </React.Fragment>
          );
        })}

        {/* Route-sight markers (sights along each transit leg) */}
        {showRouteSights && transits.flatMap(t =>
          (t.route_sights || []).map(rs => (
            <Marker
              key={`rs-${t.id}-${rs.id || rs.name}`}
              position={[rs.lat, rs.lon]}
              icon={createRouteSightIcon(rs.category)}
              eventHandlers={onRouteSightClick ? { click: () => onRouteSightClick(t.id, rs) } : undefined}
            >
              <Popup>
                <div className="min-w-[200px]">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-xs text-amber-600 font-semibold">Along the way</span>
                  </div>
                  <p className="font-bold text-sm">{rs.name}</p>
                  {rs.why_worth_it && (
                    <p className="text-xs text-gray-600 italic mt-0.5">{rs.why_worth_it}</p>
                  )}
                  <p className="text-xs text-gray-600 mt-1">{rs.description}</p>
                  <div className="flex gap-3 mt-1.5 text-xs text-gray-400">
                    {rs.detour_minutes !== undefined && <span>+{rs.detour_minutes}m detour</span>}
                    {rs.time_needed_hours !== undefined && <span>{rs.time_needed_hours}h visit</span>}
                  </div>
                  {rs.url && (
                    <a href={rs.url} target="_blank" rel="noopener noreferrer"
                       className="text-xs text-blue-500 hover:underline mt-1 block">
                      More info →
                    </a>
                  )}
                </div>
              </Popup>
            </Marker>
          ))
        )}

        {/* Side trip lines (dashed amber) */}
        {sideTrips.map(st => {
          const fromStay = stays.find(s => s.id === st.from_stay_id);
          const sg = st.suggestion;
          if (!fromStay?.location.lat || !sg.lat) return null;
          return (
            <React.Fragment key={st.id}>
              <Polyline
                positions={[
                  [fromStay.location.lat, fromStay.location.lon!],
                  [sg.lat, sg.lon!],
                ]}
                pathOptions={{ color: '#F59E0B', weight: 2, opacity: 0.7, dashArray: '5, 7' }}
              />
              {sg.lat && sg.lon && (
                <Marker position={[sg.lat, sg.lon]} icon={createSideTripIcon()}>
                  <Popup>
                    <div className="min-w-[180px]">
                      <p className="font-bold text-sm">↪ {sg.destination}</p>
                      <p className="text-xs text-gray-500 mt-1">{sg.duration} side trip</p>
                      {sg.driving_time_minutes && (
                        <p className="text-xs text-gray-500">{formatDuration(sg.driving_time_minutes)} drive</p>
                      )}
                      <p className="text-xs mt-1">{sg.description}</p>
                    </div>
                  </Popup>
                </Marker>
              )}
            </React.Fragment>
          );
        })}

        {/* Stay location markers */}
        {stays.map((stay, idx) => {
          if (!stay.location.lat || !stay.location.lon) return null;
          const color = STAY_COLORS[idx % STAY_COLORS.length];
          const isSelected = stay.id === selectedStayId;
          return (
            <Marker
              key={`stay-${stay.id}`}
              position={[stay.location.lat, stay.location.lon]}
              icon={createStayIcon(idx, stay.location.city, isSelected ? '#DC2626' : color)}
              eventHandlers={onStayClick ? { click: () => onStayClick(stay.id) } : undefined}
            >
              <Popup>
                <div className="min-w-[180px]">
                  <p className="font-bold text-sm">{stay.location.city}, {stay.location.country}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {stay.check_in ? new Date(stay.check_in).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '?'}
                    {' → '}
                    {stay.check_out ? new Date(stay.check_out).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '?'}
                    {stay.nights > 0 ? ` · ${stay.nights} night${stay.nights !== 1 ? 's' : ''}` : ''}
                  </p>
                  {stay.activities.length > 0 && (
                    <p className="text-xs text-teal-600 mt-1">{stay.activities.length} activities planned</p>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* Activity markers within enriched stays */}
        {stays.map((stay, idx) => {
          const color = STAY_COLORS[idx % STAY_COLORS.length];
          return stay.activities
            .filter(a => a.lat && a.lon)
            .map(a => (
              <Marker
                key={`act-${a.id}`}
                position={[a.lat!, a.lon!]}
                icon={createActivityIcon(color, a.category)}
              >
                <Popup>
                  <div className="min-w-[180px]">
                    <div className="flex items-center gap-1 mb-1">
                      <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                      <span className="text-xs text-gray-500">{stay.location.city} · {a.time_slot}</span>
                    </div>
                    <p className="font-bold text-sm">{a.title}</p>
                    <p className="text-xs text-gray-600 mt-1">{a.description}</p>
                    <div className="flex gap-3 mt-1.5 text-xs text-gray-400">
                      <span>{a.duration_hours}h</span>
                      {a.estimated_cost_usd !== undefined && a.estimated_cost_usd > 0 && (
                        <span>${a.estimated_cost_usd}</span>
                      )}
                    </div>
                    {a.url && (
                      <a
                        href={a.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-500 hover:underline mt-1 block"
                      >
                        More info →
                      </a>
                    )}
                  </div>
                </Popup>
              </Marker>
            ));
        })}

        {/* Map legend */}
        {stays.length > 0 && (
          <div
            className="leaflet-bottom leaflet-left"
            style={{ zIndex: 1000, pointerEvents: 'none' }}
          >
            <div className="leaflet-control bg-white dark:bg-gray-800 rounded-lg shadow-lg p-2 m-2 text-xs max-w-[180px]">
              {stays.map((s, i) => (
                <div key={s.id} className="flex items-center gap-1.5 py-0.5">
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: STAY_COLORS[i % STAY_COLORS.length] }}
                  />
                  <span className="text-gray-700 dark:text-gray-300 truncate">
                    {i + 1}. {s.location.city}
                    {s.nights > 0 ? ` (${s.nights}n)` : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </MapContainer>
    </div>
  );
};
